import {
	type InfiniteData,
	useInfiniteQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { queryKeys } from "@/api/query-keys";
import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";

import { createInstallation, listLocations } from "./installation-api";

type LocationPage = Awaited<ReturnType<typeof listLocations>>;

/** Mounted per organization; selection changes discard the form and late results. */
export function CreateInstallation({ orgCode }: { orgCode: string }) {
	const [open, setOpen] = useState(false);
	const [externalId, setExternalId] = useState("");
	const [location, setLocation] = useState("");
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<null | string>(null);
	const [notice, setNotice] = useState<null | string>(null);
	const mounted = useRef(false);
	const busy = useRef(false);
	const client = useQueryClient();
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	const locations = useInfiniteQuery<
		LocationPage,
		Error,
		InfiniteData<LocationPage>,
		ReturnType<typeof queryKeys.installationLocations>,
		string | undefined
	>({
		enabled: open,
		getNextPageParam: (last) => (last.hasMore ? last.endCursor : undefined),
		initialPageParam: undefined,
		queryFn: ({ pageParam }) => listLocations(orgCode, pageParam),
		queryKey: queryKeys.installationLocations(orgCode),
	});

	const submit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (busy.current || !externalId.trim() || !location) return;
		busy.current = true;
		setPending(true);
		setError(null);
		try {
			await createInstallation(
				{ externalId: externalId.trim(), location },
				orgCode,
			);
			if (!mounted.current) return;
			await client.invalidateQueries({
				queryKey: queryKeys.installations(orgCode),
			});
			if (!mounted.current) return;
			setOpen(false);
			setExternalId("");
			setLocation("");
			setNotice(
				"Installation created. Select it below to issue an authorization token.",
			);
		} catch {
			if (mounted.current)
				setError(
					"Could not create installation. Check the ID and location, then refresh the list before retrying.",
				);
		} finally {
			busy.current = false;
			if (mounted.current) setPending(false);
		}
	};

	return (
		<div className="my-4 space-y-3">
			{!open && (
				<Button
					onClick={() => {
						setOpen(true);
						setNotice(null);
					}}
					size="sm"
					type="button"
				>
					Add installation
				</Button>
			)}
			{notice && (
				<p className="text-sm text-success-primary" role="status">
					{notice}
				</p>
			)}
			{open && (
				<form
					className="space-y-3 rounded-lg border border-secondary p-4"
					onSubmit={submit}
				>
					<Input
						hideRequiredIndicator
						isDisabled={pending}
						isRequired
						label="Installation ID"
						onChange={setExternalId}
						value={externalId}
					/>
					<NativeSelect
						disabled={pending || locations.isLoading}
						label="Location"
						onChange={(e) => setLocation(e.target.value)}
						options={[
							{ label: "Select a location…", value: "" },
							...(locations.data?.pages ?? [])
								.flatMap((page) => page.data)
								.map((item) => ({ label: item.name, value: item.id })),
						]}
						required
						value={location}
					/>
					{locations.hasNextPage && (
						<Button
							color="secondary"
							isDisabled={locations.isFetchingNextPage}
							onClick={() => locations.fetchNextPage()}
							size="sm"
							type="button"
						>
							Load more locations
						</Button>
					)}
					{locations.error && (
						<p className="text-error-primary" role="alert">
							Could not load locations.
						</p>
					)}
					{locations.data &&
						!locations.data.pages.some((page) => page.data.length) && (
							<p className="text-sm text-secondary">
								Create a location for this organization before adding an
								installation.
							</p>
						)}
					{error && (
						<p className="text-error-primary" role="alert">
							{error}
						</p>
					)}
					<div className="flex gap-2">
						<Button
							isDisabled={pending || !externalId.trim() || !location}
							size="sm"
							type="submit"
						>
							{pending ? "Creating…" : "Create installation"}
						</Button>
						<Button
							color="secondary"
							isDisabled={pending}
							onClick={() => {
								setOpen(false);
								setError(null);
							}}
							size="sm"
							type="button"
						>
							Cancel
						</Button>
					</div>
				</form>
			)}
		</div>
	);
}
